import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store/store';
import { FetchStatusType } from '../../../types/interfaces';
import { calculateCartPrices } from '../../../features/pricing';

export const selectItemCartAddDeleteStatus = (state: RootState) =>
  state.cart.loadingId;

export const selectCartStatus = (state: RootState): FetchStatusType =>
  state.cart.status;

/**
 * `true` только при ПЕРВОМ заходе на страницу, пока корзина ещё ни
 * разу не пришла. После первого fulfilled — навсегда `false`, даже
 * если пользователь меняет количество товаров (и slice временно
 * выставляет status='loading').
 *
 * Зачем: страница `Cart.tsx` обёрнута в `<LoadingWrapper status=...>`,
 * а тот при `status==='loading'` выкидывает детей и рисует фулскрин-
 * спиннер. Слайс корзины наивно ставит `status='loading'` на ЛЮБУЮ
 * мутацию (addItemToCart, deleteItemFromCart и т.д.) — поэтому
 * каждое нажатие «+/-» на количестве вызывало мигание всей страницы.
 *
 * Per-item индикатор у нас уже есть отдельный — `state.cart.loadingId`,
 * см. `selectItemCartAddDeleteStatus`. Им и пользуемся в виджетах
 * товаров, а полностраничный лоадер оставляем только под initial load.
 */
export const selectCartInitialLoading = (state: RootState): FetchStatusType =>
  state.cart.status === 'loading' && state.cart.items.length === 0
    ? 'loading'
    : 'idle';

export const selectCartError = (state: RootState): string | null =>
  state.cart.error;

const selectCartItems = (state: RootState) => state.cart.items;

export const getAmountPreviousOrder = (state: RootState) =>
  state.cart.amount_previous_order;

export const selectTotalItemsCount = (itemId: number) =>
  createSelector(
    [selectCartItems, getAmountPreviousOrder],
    (cartItems, APO) =>
      cartItems.reduce((total, item) => {
        if (!item.do_not_count && item.id !== itemId) {
          return total + item.quantity;
        }
        return total;
      }, APO)
  );

export const selectCartWithCalculatedPrices = createSelector(
  [selectCartItems, getAmountPreviousOrder],
  (cartItems, APO) => calculateCartPrices(cartItems, APO)
);

export const selectCartItemsLength = (state: RootState) =>
  state.cart.items.length;
